import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import app, { db } from "../firebase";

const functions = getFunctions(app);

const ROLES_PARTES = [
  "Central de Alarmas",
  "Ayudante General",
  "Ayudante de Cuerpo",
  "Ayudante de Compañía",
];

const esRolCompania = (rol) => rol === "Ayudante de Compañía";

export default function GestionUsuariosPartes({ cuerpoActualId, rolActor }) {
  const esSuperadmin = rolActor === "superadmin";

  const [usuarios, setUsuarios] = useState([]);
  const [cuerpos, setCuerpos] = useState([]);
  const [companias, setCompanias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState("");
  const [filtroCuerpo, setFiltroCuerpo] = useState(esSuperadmin ? "todos" : (cuerpoActualId || ""));

  const [nombre, setNombre] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState("Central de Alarmas");
  const [cuerpoId, setCuerpoId] = useState(cuerpoActualId || "");
  const [companiaId, setCompaniaId] = useState("");

  const [editando, setEditando] = useState(null);
  const [editNombre, setEditNombre] = useState("");
  const [editLogin, setEditLogin] = useState("");
  const [editRol, setEditRol] = useState("Central de Alarmas");
  const [editCuerpoId, setEditCuerpoId] = useState("");
  const [editCompaniaId, setEditCompaniaId] = useState("");
  const [editActivo, setEditActivo] = useState(true);

  useEffect(() => {
    if (cuerpoActualId) {
      setCuerpoId(cuerpoActualId);
      if (!esSuperadmin) setFiltroCuerpo(cuerpoActualId);
    }
  }, [cuerpoActualId, esSuperadmin]);

  useEffect(() => {
    const unsubC = onSnapshot(collection(db, "cuerpos_bomberos"), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      arr.sort((a, b) => Number(a.orden || 9999) - Number(b.orden || 9999) || String(a.nombre || "").localeCompare(String(b.nombre || "")));
      setCuerpos(arr);
    });
    const unsubComp = onSnapshot(collection(db, "companias"), (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      arr.sort((a,b) => Number(a.orden || 9999) - Number(b.orden || 9999) || String(a.nombre || "").localeCompare(String(b.nombre || "")));
      setCompanias(arr);
    });
    return () => { unsubC(); unsubComp(); };
  }, []);

  async function cargarUsuarios() {
    setCargando(true);
    try {
      const fn = httpsCallable(functions, "listarUsuariosPartesSeguro");
      const res = await fn({ cuerpoId: esSuperadmin && filtroCuerpo !== "todos" ? filtroCuerpo : undefined });
      const arr = Array.isArray(res.data?.usuarios) ? res.data.usuarios : [];
      arr.sort((a,b) => String(a.cuerpoNombre || a.cuerpo_id || "").localeCompare(String(b.cuerpoNombre || b.cuerpo_id || "")) || String(a.usuario || a.email || "").localeCompare(String(b.usuario || b.email || "")));
      setUsuarios(arr);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar Usuarios de Partes: " + (e?.message || e));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargarUsuarios(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filtroCuerpo, cuerpoActualId, rolActor]);

  const cuerpoNombre = useMemo(() => {
    const map = new Map(cuerpos.map((c) => [c.id, c.nombre || c.id]));
    return (id) => map.get(id) || id || "—";
  }, [cuerpos]);

  const companiasCrear = useMemo(() => companias.filter((c) => c.cuerpo_id === cuerpoId && c.activo !== false), [companias, cuerpoId]);
  const companiasEditar = useMemo(() => companias.filter((c) => c.cuerpo_id === editCuerpoId && c.activo !== false), [companias, editCuerpoId]);

  async function crear() {
    if (!nombre.trim() || !login.trim() || password.length < 6 || !rol || !cuerpoId) {
      alert("Completa nombre, usuario, contraseña (mínimo 6), rol y Cuerpo.");
      return;
    }
    if (esRolCompania(rol) && !companiaId) {
      alert("Ayudante de Compañía requiere una compañía.");
      return;
    }
    setProcesando("crear");
    try {
      const fn = httpsCallable(functions, "crearUsuarioPartesSeguro");
      await fn({ nombre: nombre.trim(), login: login.trim(), password, rol, cuerpoId, companiaId: esRolCompania(rol) ? companiaId : null });
      setNombre(""); setLogin(""); setPassword(""); setRol("Central de Alarmas"); setCompaniaId("");
      await cargarUsuarios();
      alert("Usuario de Partes creado correctamente.");
    } catch (e) {
      console.error(e);
      alert("No se pudo crear el usuario: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  function abrirEditar(u) {
    setEditando(u);
    setEditNombre(u.usuario || u.nombre || "");
    setEditLogin(u.login || u.email || "");
    setEditRol(ROLES_PARTES.includes(u.rol) ? u.rol : "Central de Alarmas");
    setEditCuerpoId(u.cuerpo_id || cuerpoActualId || "");
    setEditCompaniaId(u.compania_id || "");
    setEditActivo(u.activo !== false);
  }

  async function guardarEdicion() {
    if (!editando) return;
    if (!editNombre.trim() || !editLogin.trim() || !editRol || !editCuerpoId) {
      alert("Nombre, usuario, rol y Cuerpo son obligatorios.");
      return;
    }
    if (esRolCompania(editRol) && !editCompaniaId) {
      alert("Ayudante de Compañía requiere una compañía.");
      return;
    }
    setProcesando(editando.uid || editando.id);
    try {
      const fn = httpsCallable(functions, "actualizarUsuarioPartesSeguro");
      await fn({
        uid: editando.uid || editando.id,
        nombre: editNombre.trim(),
        login: editLogin.trim(),
        rol: editRol,
        cuerpoId: editCuerpoId,
        companiaId: esRolCompania(editRol) ? editCompaniaId : null,
        activo: editActivo,
      });
      setEditando(null);
      await cargarUsuarios();
      alert("Usuario de Partes actualizado.");
    } catch (e) {
      console.error(e);
      alert("No se pudo actualizar: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  async function cambiarClave(u) {
    const p1 = window.prompt(`Nueva contraseña para ${u.usuario || u.email || u.uid} (mínimo 6 caracteres):`);
    if (p1 == null) return;
    if (p1.length < 6) { alert("La contraseña debe tener al menos 6 caracteres."); return; }
    const p2 = window.prompt("Repite la nueva contraseña:");
    if (p1 !== p2) { alert("Las contraseñas no coinciden."); return; }
    setProcesando(u.uid || u.id);
    try {
      const fn = httpsCallable(functions, "cambiarPasswordUsuarioPartesSeguro");
      await fn({ uid: u.uid || u.id, password: p1 });
      alert("Contraseña actualizada.");
    } catch (e) {
      console.error(e);
      alert("No se pudo cambiar la contraseña: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  async function eliminar(u) {
    if (!window.confirm(`¿Eliminar el acceso a Partes de ${u.usuario || u.email || u.uid}?\n\nSi esta cuenta solo se usa para Partes, también se eliminará de Firebase Authentication.`)) return;
    setProcesando(u.uid || u.id);
    try {
      const fn = httpsCallable(functions, "eliminarUsuarioPartesSeguro");
      const res = await fn({ uid: u.uid || u.id });
      await cargarUsuarios();
      alert(res.data?.authEliminado === false
        ? "Perfil de Partes eliminado. La cuenta Auth se conservó porque también tiene acceso administrativo."
        : "Usuario de Partes eliminado correctamente.");
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar: " + (e?.message || e));
    } finally { setProcesando(""); }
  }

  const usuariosVisibles = useMemo(() => {
    if (!esSuperadmin) return usuarios;
    if (filtroCuerpo === "todos") return usuarios;
    return usuarios.filter((u) => u.cuerpo_id === filtroCuerpo);
  }, [usuarios, esSuperadmin, filtroCuerpo]);

  return (
    <div style={{ maxWidth: 1350 }}>
      <h2>Usuarios de Partes</h2>
      <p>
        Gestiona los accesos al módulo Partes de Emergencia. {esSuperadmin
          ? "Como superadmin puedes administrar usuarios de todos los Cuerpos."
          : "Como administrador solo puedes administrar usuarios de tu Cuerpo de Bomberos."}
      </p>

      {esSuperadmin && (
        <div style={{ marginBottom: 16, maxWidth: 430 }}>
          <label style={{ fontWeight: 700, display: "block", marginBottom: 5 }}>Mostrar usuarios</label>
          <select value={filtroCuerpo} onChange={(e) => setFiltroCuerpo(e.target.value)} style={{ width: "100%" }}>
            <option value="todos">Todos los Cuerpos</option>
            {cuerpos.map((c) => <option key={c.id} value={c.id}>{c.nombre || c.id}</option>)}
          </select>
        </div>
      )}

      <div style={{ border: "1px solid #ddd", borderRadius: 10, padding: 16, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Crear usuario de Partes</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr auto", gap: 8 }}>
          <input placeholder="Nombre visible" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <input placeholder="Usuario (ej. 16309 o correo)" value={login} onChange={(e) => setLogin(e.target.value)} />
          <input type="password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} />
          <select value={rol} onChange={(e) => { setRol(e.target.value); if (!esRolCompania(e.target.value)) setCompaniaId(""); }}>
            {ROLES_PARTES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={cuerpoId} disabled={!esSuperadmin} onChange={(e) => { setCuerpoId(e.target.value); setCompaniaId(""); }}>
            <option value="">-- Cuerpo --</option>
            {cuerpos.map((c) => <option key={c.id} value={c.id}>{c.nombre || c.id}</option>)}
          </select>
          <button onClick={crear} disabled={procesando === "crear"}>{procesando === "crear" ? "Creando..." : "Crear"}</button>
        </div>
        <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
          Si escribes solo un usuario (por ejemplo 16309), el acceso será <b>16309@de.cl</b>. También puedes escribir un correo completo.
        </div>
        {esRolCompania(rol) && (
          <div style={{ marginTop: 10, maxWidth: 450 }}>
            <label style={{ fontWeight: 700, display: "block", marginBottom: 4 }}>Compañía</label>
            <select value={companiaId} onChange={(e) => setCompaniaId(e.target.value)} style={{ width: "100%" }}>
              <option value="">-- Seleccionar compañía --</option>
              {companiasCrear.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
        )}
      </div>

      {editando && (
        <div style={{ border: "2px solid #8aa8c2", borderRadius: 10, padding: 16, marginBottom: 24, background: "#f7fbff" }}>
          <h3 style={{ marginTop: 0 }}>Editar usuario de Partes</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
            <input value={editNombre} onChange={(e) => setEditNombre(e.target.value)} placeholder="Nombre visible" />
            <input value={editLogin} onChange={(e) => setEditLogin(e.target.value)} placeholder="Usuario o correo" />
            <select value={editRol} onChange={(e) => { setEditRol(e.target.value); if (!esRolCompania(e.target.value)) setEditCompaniaId(""); }}>
              {ROLES_PARTES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={editCuerpoId} disabled={!esSuperadmin} onChange={(e) => { setEditCuerpoId(e.target.value); setEditCompaniaId(""); }}>
              {cuerpos.map((c) => <option key={c.id} value={c.id}>{c.nombre || c.id}</option>)}
            </select>
          </div>
          {esRolCompania(editRol) && (
            <div style={{ marginTop: 10, maxWidth: 450 }}>
              <label style={{ fontWeight: 700, display: "block", marginBottom: 4 }}>Compañía</label>
              <select value={editCompaniaId} onChange={(e) => setEditCompaniaId(e.target.value)} style={{ width: "100%" }}>
                <option value="">-- Seleccionar compañía --</option>
                {companiasEditar.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          <label style={{ display: "block", marginTop: 12 }}>
            <input type="checkbox" checked={editActivo} onChange={(e) => setEditActivo(e.target.checked)} /> Usuario activo
          </label>
          <div style={{ marginTop: 12 }}>
            <button onClick={guardarEdicion} disabled={procesando === (editando.uid || editando.id)}>Guardar cambios</button>{" "}
            <button onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      )}

      <h3>Usuarios registrados</h3>
      {cargando ? <p>Cargando usuarios...</p> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={th}>Nombre</th><th style={th}>Usuario / correo</th><th style={th}>Rol</th><th style={th}>Cuerpo</th><th style={th}>Compañía</th><th style={th}>Estado</th><th style={th}>Acciones</th>
            </tr></thead>
            <tbody>
              {usuariosVisibles.map((u) => (
                <tr key={u.uid || u.id}>
                  <td style={td}>{u.usuario || u.nombre || "—"}</td>
                  <td style={td}>{u.login || u.email || u.uid || u.id}</td>
                  <td style={td}>{u.rol || "—"}</td>
                  <td style={td}>{u.cuerpoNombre || cuerpoNombre(u.cuerpo_id)}</td>
                  <td style={td}>{u.compania || "—"}</td>
                  <td style={td}>{u.activo === false ? "Inactivo" : "Activo"}</td>
                  <td style={td}>
                    <button onClick={() => abrirEditar(u)} disabled={procesando === (u.uid || u.id)}>Editar</button>{" "}
                    <button onClick={() => cambiarClave(u)} disabled={procesando === (u.uid || u.id)}>Cambiar contraseña</button>{" "}
                    <button onClick={() => eliminar(u)} disabled={procesando === (u.uid || u.id)} style={{ color: "#b00020" }}>Eliminar</button>
                  </td>
                </tr>
              ))}
              {!usuariosVisibles.length && <tr><td style={td} colSpan={7}>No hay Usuarios de Partes registrados.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { textAlign: "left", borderBottom: "2px solid #ccc", padding: 10 };
const td = { borderBottom: "1px solid #ddd", padding: 10, verticalAlign: "top" };
